import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteJobTitle, useSaveJobTitle } from '../../mutations';
import { useJobTitles } from '../../queries';
import type { JobTitle } from '../../types';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { Input } from '@/shared/ui/atoms/Input';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import '../RolesManager/RolesManager.styl';

const valid = (name: string) => name.trim().length >= 2 && name.trim().length <= 80;

/** The organisation's job title catalogue: members and invitations choose from it. */
export function JobTitlesManager() {
  const { t } = useTranslation(undefined, { keyPrefix: 'jobTitles' });
  const titles = useJobTitles();
  const save = useSaveJobTitle();
  const toast = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (titles.isPending) return <Spinner label={t('loading')} />;

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!valid(name)) return setError(t('nameInvalid'));
    setError(null);
    save.mutate(
      { name: name.trim() },
      {
        onSuccess: () => {
          setName('');
          toast.show(t('created'), 'success');
        },
        onError: (err) => setError(errorMessage(err, t('saveError')))
      }
    );
  };

  return (
    <div className="f-roles">
      <p className="f-roles__hint">{t('intro')}</p>
      <form className="f-roles__bar" onSubmit={add} noValidate>
        <Input icon="user-tie" aria-label={t('name')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('placeholder')} />
        <Button variant="add" type="submit" size="sm" iconLeft={<FontIcon name="plus-circle" />} loading={save.isPending}>
          {t('add')}
        </Button>
      </form>
      {error ? (
        <div className="f-newtask__error" role="alert">
          {error}
        </div>
      ) : null}
      {(titles.data ?? []).length === 0 ? (
        <p className="f-roles__hint">{t('empty')}</p>
      ) : (
        <ul className="f-roles__list">
          {(titles.data ?? []).map((jt) => (
            <JobTitleRow key={jt.id} title={jt} />
          ))}
        </ul>
      )}
    </div>
  );
}

function JobTitleRow({ title }: { title: JobTitle }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'jobTitles' });
  const save = useSaveJobTitle();
  const remove = useDeleteJobTitle();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState(title.name);

  const rename = (e: FormEvent) => {
    e.preventDefault();
    if (!valid(name)) return toast.show(t('nameInvalid'), 'error');
    save.mutate(
      { id: title.id, name: name.trim() },
      {
        onSuccess: () => {
          setEditing(false);
          toast.show(t('renamed'), 'success');
        },
        onError: (err) => toast.show(errorMessage(err, t('saveError')), 'error')
      }
    );
  };

  if (editing) {
    return (
      <li className="f-roles__item">
        <form className="f-roles__bar" onSubmit={rename} noValidate>
          <Input icon="user-tie" aria-label={t('name')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <Button type="submit" size="sm" loading={save.isPending}>
            {t('save')}
          </Button>
          <Button size="sm" variant="cancel" onClick={() => { setName(title.name); setEditing(false); }}>
            {t('cancel')}
          </Button>
        </form>
      </li>
    );
  }

  return (
    <li className="f-roles__item">
      <div className="f-roles__main">
        <div className="f-roles__name">{title.name}</div>
      </div>
      <div className="f-roles__actions">
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          {t('rename')}
        </Button>
        {confirming ? (
          <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(title.id, { onSuccess: () => toast.show(t('deleted'), 'success'), onError: (err) => toast.show(errorMessage(err, t('deleteError')), 'error'), onSettled: () => setConfirming(false) })}>
            {t('confirmDelete')}
          </Button>
        ) : (
          <Button size="sm" variant="ghost" iconLeft={<FontIcon name="bin" />} onClick={() => setConfirming(true)}>
            {t('delete')}
          </Button>
        )}
      </div>
    </li>
  );
}
